import store from '@/store/index'
import { APK_VERSION_NAME_MAP } from '@/constants/app-version-map'

const splitList = (raw: string | null | undefined): string[] =>
    (raw || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

const currentUserId = (): string => {
    const id = (store.state as any).userInfo?.details?.id
    return id != null ? String(id) : ''
}

// Which reprint dialog the current cashier gets.
//
// A cashier is on the NEW printer-status flow if EITHER opt-in is true:
//   • the global switch `using_reprint_dialog` (store.usingReprintDialog) is on, OR
//   • the per-user list `users_new_reprint_dialog` (store.usersNewReprintDialog)
//     opts them in:
//       ''            → nobody via the list (every cashier OLD unless the switch is on)
//       'all'         → every cashier NEW
//       '111,222,333' → only those cashier ids NEW; everyone else OLD
//
// The whole new printer-status flow (dialog-driven printing, pre-purchase printer
// pre-flight, print-status reporting) is gated on this so the OLD behaviour is
// preserved verbatim for everyone not opted in.
export const isNewReprintCashier = (): boolean => {
    if ((store.state as any).usingReprintDialog === true) return true
    const raw = (store.state as any).usersNewReprintDialog
    if (typeof raw === 'string' && raw.trim().toLowerCase() === 'all') return true
    const me = currentUserId()
    if (!me) return false
    return splitList(raw).includes(me)
}

// Minimum APK the NEW reprint flow ships in. On-list cashiers must run >= this;
// off-list cashiers must run < this (they only have the OLD flow).
export const REQUIRED_APK_FOR_NEW_REPRINT = '3.0.0'

// Parse a version string into numeric parts for comparison.
// Handles both dotted names ('3.0.0', '2.0.2') and packed versionCodes
// ('300', '202') as seen in APK_VERSION_NAME_MAP / native device-info.
// Parsing is strict: only clean, fully-numeric formats are accepted. Anything
// unrecognised (null, '', 'v3.0.0', '3.0.0-rc1', garbage) returns null so the
// caller can fail open — an off-list cashier on an old APK must never be blocked
// just because the version string is missing or in an unexpected format.
export const parseVersion = (raw: string | null | undefined): number[] | null => {
    const mapped = raw != null ? APK_VERSION_NAME_MAP[raw] : undefined
    const s = (mapped ?? raw ?? '').toString().trim()
    if (!s) return null
    if (s.includes('.')) {
        // Clean dotted version: every dot-separated part must be all digits.
        if (!/^\d+(\.\d+)+$/.test(s)) return null
        return s.split('.').map((n) => parseInt(n, 10))
    }
    // Packed versionCode: major*100 + minor*10 + patch (e.g. 202 -> 2.0.2).
    if (!/^\d+$/.test(s)) return null
    const n = parseInt(s, 10)
    return [Math.floor(n / 100), Math.floor(n / 10) % 10, n % 10]
}

export const compareVersion = (a: number[], b: number[]): number => {
    const len = Math.max(a.length, b.length)
    for (let i = 0; i < len; i++) {
        const d = (a[i] || 0) - (b[i] || 0)
        if (d !== 0) return d < 0 ? -1 : 1
    }
    return 0
}

// Whether the running APK is compatible with this cashier's reprint mode:
//   on-list  cashier -> APK must be >= REQUIRED_APK_FOR_NEW_REPRINT
//   off-list cashier -> APK must be  < REQUIRED_APK_FOR_NEW_REPRINT
// When the APK version cannot be read (null, empty, or an unexpected format such
// as 'v3.0.0'), the check is skipped (returns true) so nobody is locked out — in
// particular an off-list cashier keeps selling on their old APK.
export const isApkCompatibleWithReprintMode = (): boolean => {
    const parsed = parseVersion((store.state as any).apkVersionNumber)
    if (!parsed) return true
    const cmp = compareVersion(parsed, parseVersion(REQUIRED_APK_FOR_NEW_REPRINT) as number[])
    return isNewReprintCashier() ? cmp >= 0 : cmp < 0
}
